import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { authService } from '../services/api';
import { Hotel as HotelIcon } from 'lucide-react';

function SignupPage({ onSignup, onBack }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const signupData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        gender: formData.gender || undefined,
        password: formData.password,
        role: 'GUEST'
      };

      const response = await authService.register(signupData);
      
      if (response.success) {
        toast.success('Account created successfully!');
        onSignup(response.data.user, response.data.token);
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error.error || error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#EEEBE1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{ 
        maxWidth: '500px', 
        width: '100%',
        padding: '40px 30px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        border: '1px solid #E3DFD3'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: '16px',
            background: '#1A3E2A',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            <HotelIcon size="1em" aria-hidden="true" />
          </div>
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#24332A',
            marginBottom: '8px'
          }}>
            Create Your Account
          </h2>
          <p style={{ 
            color: '#5F6B61', 
            fontSize: '16px',
            margin: 0
          }}>
            Join us for an amazing retreat experience
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <style>
            {`
              .signup-input::placeholder {
                color: #A5A89B !important;
                opacity: 1;
              }
              .signup-input::-webkit-input-placeholder {
                color: #A5A89B !important;
              }
              .signup-input::-moz-placeholder {
                color: #A5A89B !important;
                opacity: 1;
              }
              .signup-input:-ms-input-placeholder {
                color: #A5A89B !important;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#36433B',
              fontSize: '14px'
            }}>
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className="signup-input"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: errors.name ? '2px solid #A13F2F' : '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                backgroundColor: 'white',
                color: '#24332A',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1A3E2A';
                e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.name ? '#A13F2F' : '#E3DFD3';
                e.target.style.boxShadow = 'none';
              }}
            />
            {errors.name && (
              <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.name}
              </span>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#36433B',
              fontSize: '14px'
            }}>
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              className="signup-input"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: errors.email ? '2px solid #A13F2F' : '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                backgroundColor: 'white',
                color: '#24332A',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1A3E2A';
                e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.email ? '#A13F2F' : '#E3DFD3';
                e.target.style.boxShadow = 'none';
              }}
            />
            {errors.email && (
              <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.email}
              </span>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#36433B',
              fontSize: '14px'
            }}>
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter your phone number"
              className="signup-input"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                backgroundColor: 'white',
                color: '#24332A',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1A3E2A';
                e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E3DFD3';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#36433B',
              fontSize: '14px'
            }}>
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              className="signup-input"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                backgroundColor: 'white',
                color: '#24332A',
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1A3E2A';
                e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E3DFD3';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="" style={{ color: '#A5A89B' }}>Select your gender</option>
              <option value="MALE" style={{ color: '#24332A' }}>Male</option>
              <option value="FEMALE" style={{ color: '#24332A' }}>Female</option>
              <option value="OTHER" style={{ color: '#24332A' }}>Other</option>
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#36433B',
              fontSize: '14px'
            }}>
              Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Create a password (min. 6 characters)"
              className="signup-input"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: errors.password ? '2px solid #A13F2F' : '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                backgroundColor: 'white',
                color: '#24332A',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1A3E2A';
                e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.password ? '#A13F2F' : '#E3DFD3';
                e.target.style.boxShadow = 'none';
              }}
            />
            {errors.password && (
              <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.password}
              </span>
            )}
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#36433B',
              fontSize: '14px'
            }}>
              Confirm Password *
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your password"
              className="signup-input"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: errors.confirmPassword ? '2px solid #A13F2F' : '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                backgroundColor: 'white',
                color: '#24332A',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1A3E2A';
                e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.confirmPassword ? '#A13F2F' : '#E3DFD3';
                e.target.style.boxShadow = 'none';
              }}
            />
            {errors.confirmPassword && (
              <span style={{ color: '#A13F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                flex: 1,
                padding: '14px 20px',
                border: '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#5F6B61',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#5F6B61';
                e.target.style.color = '#36433B';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = '#E3DFD3';
                e.target.style.color = '#5F6B61';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              ← Back to Login
            </button>
            
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '14px 20px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                outline: 'none',
                background: loading ? '#A5A89B' : '#1A3E2A'
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.background = '#163626';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(26, 62, 42, 0.3)';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.target.style.background = '#1A3E2A';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </div>
        </form>

        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px',
          padding: '16px',
          backgroundColor: '#F5F3EB',
          borderRadius: '8px',
          border: '1px solid #E3DFD3'
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: '#5F6B61',
            lineHeight: '1.5'
          }}>
            By creating an account, you agree to our{' '}
            <span style={{ color: '#1A3E2A', fontWeight: '600' }}>Terms of Service</span>
            {' '}and{' '}
            <span style={{ color: '#1A3E2A', fontWeight: '600' }}>Privacy Policy</span>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;